import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Send } from "lucide-react";
import { motion } from "framer-motion";

export default function ChatAssistant({ onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "> OBSIDIAN online. I have your latest scan open. Ask me anything about your exposure or the removal process." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "[ERROR] channel disrupted. Try again." }]);
    } finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="fixed bottom-6 right-6 z-50 flex h-[520px] w-full max-w-md flex-col border border-white/15 bg-black/90 backdrop-blur-xl shadow-2xl">
      <div className="flex items-center justify-between border-b border-white/10 bg-black/70 px-4 py-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5FF00]">/ OBSIDIAN · LIVE</div>
          <div className="mt-0.5 font-display text-sm font-medium">AI Wizard Chat</div>
        </div>
        <button data-testid="chat-close" onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4 font-mono text-xs leading-relaxed">
        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${i}`} className={m.role === "user" ? "text-zinc-100" : "text-[#E5FF00]"}>
            <div className="mb-1 text-[9px] uppercase tracking-[0.25em] text-zinc-500">{m.role === "user" ? "YOU" : "OBSIDIAN"}</div>
            <div className="whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
        {busy && <div className="text-[#E5FF00] caret"></div>}
      </div>

      <form onSubmit={send} className="flex items-center gap-2 border-t border-white/10 bg-black/70 p-3">
        <input data-testid="chat-input"
          className="flex-1 border border-white/10 bg-black px-3 py-2 font-mono text-xs text-white focus:border-[#E5FF00] focus:outline-none"
          placeholder="Type a message…" value={input} onChange={e => setInput(e.target.value)} />
        <button data-testid="chat-send" type="submit" disabled={busy}
          className="btn-cta border border-[#E5FF00] bg-[#E5FF00] p-2.5 text-black hover:bg-transparent hover:text-[#E5FF00] transition-colors">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </motion.div>
  );
}
